/**
 * IHS-4 Last 5 games — BOTH teams, always. One widget, two layouts.
 *
 * 2026-07-09 (Johan): the home/away tab is gone. Hiding half the data behind
 * an interaction was wrong on both surfaces — in a 9:16 Stories frame the
 * viewer cannot tap at all, and on-site it forced a click to compare the two
 * things the page exists to compare.
 *
 * This file replaces the former IhsLast5Games + IhsLast5GamesCompact pair
 * (two components, ~300 lines, one tab-state machine each) with a single
 * component and a `layout` switch:
 *
 *   layout="stacked" (on-site)  — team blocks one under the other, full width,
 *                                 opponent + score + date per row.
 *   layout="columns" (Stories)  — team blocks side by side, date dropped; the
 *                                 tick rail already reads oldest → newest.
 *
 * Each block: team identity → form ticks (the shared EventHistoryStrip in
 * three-state mode) → the result rows.
 */

import type { ReactElement } from 'react';
import { useTranslation } from '../../../i18n/index.js';
import { useTenantConfig } from '../../../tenant/useTenantConfig.js';
import type {
  IhsLastFiveGamesData,
  IhsLastFiveGamesSide,
  IhsLastGameEntry,
} from '../../../types/ihs.js';
import { ScorePill } from '../../primitives/ScorePill.js';
import { TeamCrest } from '../../primitives/TeamCrest.js';
import {
  EventHistoryStrip,
  type EventHistoryOutcome,
} from '../../primitives/EventHistoryStrip.js';
import { IhsCard } from './IhsCard.js';
import styles from './IhsLast5Games.module.css';

export type Last5Layout = 'stacked' | 'columns';

interface MarketContext {
  locale: string;
  timezone: string;
}

function useMarketContext(): MarketContext {
  const tenant = useTenantConfig();
  if (tenant.kind !== 'live' && tenant.kind !== 'paused') {
    // Tenant-neutral fallback — fires only in non-live boot states where the
    // chrome shouldn't render anyway.
    return { locale: 'en-GB', timezone: 'UTC' };
  }
  return {
    locale: tenant.config.market.locale,
    timezone: tenant.config.market.timezone,
  };
}

/** The opponent is whichever side the perspective team is not. */
function opponentOf(g: IhsLastGameEntry): string {
  return g.perspective === 'home' ? g.awayTeamName : g.homeTeamName;
}

function outcomeToState(o: 'W' | 'D' | 'L'): 'win' | 'draw' | 'loss' {
  if (o === 'W') return 'win';
  if (o === 'L') return 'loss';
  return 'draw';
}

/** Short day/month in the tenant's locale + timezone. Never a hardcoded format. */
function shortDate(iso: string, market: MarketContext): string {
  const d = new Date(`${iso}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return '';
  try {
    return new Intl.DateTimeFormat(market.locale, {
      day: '2-digit',
      month: '2-digit',
      timeZone: market.timezone,
    }).format(d);
  } catch {
    return '';
  }
}

interface BlockProps {
  side: IhsLastFiveGamesSide;
  layout: Last5Layout;
  market: MarketContext;
  homeShort: string;
  awayShort: string;
  formLabel: string;
}

function TeamBlock({
  side,
  layout,
  market,
  homeShort,
  awayShort,
  formLabel,
}: BlockProps): ReactElement {
  const games = side.games.slice(0, 5);
  // Engine ships newest → oldest; the tick rail reads oldest → newest so the
  // widest, full-opacity tick is the most recent result.
  const outcomes: EventHistoryOutcome[] = [...games].reverse().map((g) => ({
    state: outcomeToState(g.outcome),
    label: `${opponentOf(g)} ${g.scoreLine}`,
  }));
  const showDate = layout === 'stacked';

  return (
    <div className={styles.block}>
      <div className={styles.teamHead}>
        <TeamCrest
          {...(side.team.badge !== undefined ? { src: side.team.badge } : {})}
          name={side.team.name}
          size={layout === 'columns' ? 20 : 24}
        />
        <span className={styles.teamName} title={side.team.name}>
          {side.team.name}
        </span>
      </div>

      {outcomes.length > 0 ? (
        <EventHistoryStrip
          outcomes={outcomes}
          mode="three-state"
          size="sm"
          ariaLabel={`${formLabel}: ${side.team.name}`}
        />
      ) : null}

      <ul className={styles.list}>
        {games.map((g, i) => (
          <li key={`${g.sportEventUrn ?? g.date}-${i}`} className={styles.gameRow}>
            <span className={styles.venue} aria-hidden="true">
              {g.perspective === 'home' ? homeShort : awayShort}
            </span>
            <span className={styles.opponent} title={opponentOf(g)}>
              {opponentOf(g)}
            </span>
            <ScorePill scoreLine={g.scoreLine} outcome={g.outcome} size="sm" />
            {showDate ? (
              <span className={styles.date}>{shortDate(g.date, market)}</span>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

interface Last5Props {
  data: IhsLastFiveGamesData;
  layout?: Last5Layout;
}

function Last5({ data, layout = 'stacked' }: Last5Props): ReactElement | null {
  const { t } = useTranslation();
  const market = useMarketContext();

  // Stories rule (user verdict 2026-05-13): a 9:16 overlay with no data must
  // drop ENTIRELY — an empty card would still ship its chrome and float above
  // the odds strip. On-site there is no such constraint: the card stays and
  // shows its empty state, which is what the slot test asserts.
  const dropWhenEmpty = layout === 'columns';
  const homeGames = data?.home?.games?.length ?? 0;
  const awayGames = data?.away?.games?.length ?? 0;
  const hasAnyGames = homeGames > 0 || awayGames > 0;
  if (dropWhenEmpty && (!data?.home || !data?.away || !hasAnyGames)) return null;

  const title =
    data.header?.title ??
    t('ihs.last5Games.title', { count: 5, defaultValue: 'Last 5 games' });
  const homeShort = t('ihs.last5Games.venueHomeShort', { defaultValue: 'H' });
  const awayShort = t('ihs.last5Games.venueAwayShort', { defaultValue: 'A' });
  const formLabel = t('ihs.last5Games.title', {
    count: 5,
    defaultValue: 'Last 5 games',
  });

  // Cue-driven single-side envelopes still render: one block, full width.
  const sides: IhsLastFiveGamesSide[] = [];
  if (homeGames > 0 && data.home) sides.push(data.home);
  if (awayGames > 0 && data.away) sides.push(data.away);
  const effectiveLayout: Last5Layout = sides.length === 1 ? 'stacked' : layout;

  return (
    <IhsCard title={title} bodyVariant="tight">
      {sides.length === 0 ? (
        <p className={styles.empty}>
          {t('ihs.last5Games.empty', { defaultValue: 'No matches to show' })}
        </p>
      ) : (
        <div className={styles.blocks} data-layout={effectiveLayout}>
          {sides.map((side) => (
            <TeamBlock
              key={side.team.name}
              side={side}
              layout={effectiveLayout}
              market={market}
              homeShort={homeShort}
              awayShort={awayShort}
              formLabel={formLabel}
            />
          ))}
        </div>
      )}
    </IhsCard>
  );
}

/** On-site: full-width team blocks stacked vertically. */
export function IhsLast5Games({
  data,
}: {
  data: IhsLastFiveGamesData;
}): ReactElement | null {
  return <Last5 data={data} layout="stacked" />;
}

/** Stories / 9:16 overlays: both teams side by side, dates dropped. */
export function IhsLast5GamesCompact({
  data,
}: {
  data: IhsLastFiveGamesData;
}): ReactElement | null {
  return <Last5 data={data} layout="columns" />;
}
